// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import { Button } from '@mui/material';

// KEY DESIGN TOKENS
const BUTTON_BORDER_RADIUS = 0.5; // 4px
const SMALL_BUTTON_PADDING = '4px 10px';
const MEDIUM_BUTTON_PADDING = '8px 16px';
const GAP_BETWEEN_ICON_AND_TEXT = 0.5; // 4px

/**
 * SolyticsButton - A comprehensive button component built on MUI Button
 *
 * @component
 * @description
 * SolyticsButton is a wrapper around Material-UI's Button component that extends its functionality
 * with custom shape variants and variant styles while maintaining controlled prop exposure.
 *
 * @param {React.ReactNode} children - The content of the component
 * @param {string} variant - The variant to use: 'contained' | 'outlined' | 'text' | 'link'
 * @param {string} color - The color of the component: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
 * @param {string} size - The size of the component: 'small' | 'medium' | 'large'
 * @param {string} shape - Custom shape variant: 'pill' | 'circle' | 'square' | 'rectangle' | 'sharp' | 'block'
 * @param {boolean} disabled - If true, the component is disabled
 * @param {React.ReactNode} startIcon - Element placed before the children
 * @param {React.ReactNode} endIcon - Element placed after the children
 * @param {boolean} fullWidth - If true, the button will take up the full width
 * @param {string|number} height - Custom height for the button (e.g., '40px', 40)
 * @param {string|number} width - Custom width for the button (e.g., '200px', 200)
 * @param {string|number} minWidth - Minimum width for the button (e.g., '100px', 100)
 * @param {string|number} maxWidth - Maximum width for the button (e.g., '500px', 500)
 * @param {function} onClick - Click handler function
 * @param {string} href - The URL to link to when button is clicked
 * @param {React.ElementType} component - The component used for the root node
 * @param {string} title - Tooltip text shown on hover
 * @param {string} type - The type of button: 'button' | 'submit' | 'reset'
 * @param {string} name - Name attribute for form submission
 * @param {string} value - Value attribute for form submission
 * @param {boolean} autoFocus - If true, the button will be focused when mounted
 * @param {string} id - HTML id attribute
 * @param {string} className - Additional CSS class name
 * @param {function} textColor - Custom text color callback: (theme) => color. Drives text, icon, border, and background
 *
 * @returns {React.ReactElement} A button element
 */
const SolyticsButton = ({
  children,
  id,
  onClick,
  title,
  type = 'button',
  name,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  shape,
  disabled = false,
  startIcon,
  endIcon,
  fullWidth = false,
  minWidth,
  maxWidth,
}) => {
  const DEFAULT_BUTTON_PROPS = {
    disableElevation: true,
    disableRipple: false,
    disableFocusRipple: true,
    focusRipple: false,
    centerRipple: false,
    touchRippleRef: undefined,
    TouchRippleProps: undefined,
  };

  const getVariantStyles = (theme) => {
    const variantStyleMap = {
      text: {},
      link: {
        minWidth: 'auto',
        padding: 0,
        fontSize: '12px',
        textDecoration: 'none',
        '&&:hover': {
          backgroundColor: 'transparent !important',
          textDecoration: 'underline !important',
        },
      },
      contained: {
        borderRadius: BUTTON_BORDER_RADIUS,
        padding: `${size === 'small' ? SMALL_BUTTON_PADDING : MEDIUM_BUTTON_PADDING} !important`,
      },
      outlined: {
        padding: `${size === 'small' ? SMALL_BUTTON_PADDING : MEDIUM_BUTTON_PADDING} !important`,
        borderRadius: BUTTON_BORDER_RADIUS,
        borderColor: `${theme.palette?.divider} !important`,
        background: theme.palette.background?.default,
      },
    };
    return variantStyleMap[variant] ?? {};
  };

  const getShapeStyles = () => {
    if (!shape) return {};

    const shapeStyleMap = {
      pill: {
        borderRadius: 16,
      },
      circle: {
        borderRadius: '50%',
        minWidth: 40,
        width: 40,
        height: 40,
        padding: 0,
        '&.MuiButton-sizeSmall': {
          minWidth: 32,
          width: 32,
          height: 32,
        },
        '&.MuiButton-sizeLarge': {
          minWidth: 48,
          width: 48,
          height: 48,
        },
      },
      square: {
        borderRadius: '4px',
        minWidth: 40,
        width: 40,
        height: 40,
        padding: 0,
        '&.MuiButton-sizeSmall': {
          minWidth: 32,
          width: 32,
          height: 32,
        },
        '&.MuiButton-sizeLarge': {
          minWidth: 48,
          width: 48,
          height: 48,
        },
      },
      rectangle: {
        borderRadius: '4px',
      },
      sharp: {
        borderRadius: 0,
      },
      block: {
        width: '100%',
        height: '100%',
        display: 'block',
      },
    };

    return shapeStyleMap[shape] ?? {};
  };

  const buttonProps = {
    ...DEFAULT_BUTTON_PROPS,
    ...(type && { type }),
    ...(variant && { variant: variant === 'link' ? 'text' : variant }),
    ...(color && { color }),
    ...(size && { size }),
    ...(disabled === true ? { disabled: true } : { disabled: false }),
    ...(fullWidth && { fullWidth: fullWidth || shape === 'block' }),
    ...(title && { title }),
    ...(onClick && { onClick }),
    ...(name && { name }),
    ...(id && { id }),
    ...(startIcon && { startIcon }),
    ...(endIcon && { endIcon }),
    sx: (theme) => ({
      ...getVariantStyles(theme, size),
      ...getShapeStyles(theme),
      ...(minWidth && { minWidth }),
      ...(maxWidth && { maxWidth }),
      boxShadow: 'none',
      gap: GAP_BETWEEN_ICON_AND_TEXT,
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: 0,
      },
      '& .MuiButton-endIcon': {
        marginLeft: 0,
        marginRight: 0,
      },
      outline: 'none',
      '&:hover': {
        boxShadow: 'none',
        outline: 'none',
      },
      '&:active': {
        boxShadow: 'none',
        outline: 'none',
      },
      '&:focus': {
        boxShadow: 'none',
        outline: 'none',
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette?.primary?.main}`,
        outlineOffset: '2px',
      },
      '&.Mui-disabled': {
        borderColor: 'currentColor',
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    }),
  };

  return <Button {...buttonProps}>{children}</Button>;
};

export default SolyticsButton;

/**
 * ## Features
 * - Controlled prop exposure - only specific props are exposed
 * - Custom shape variants: pill, circle, square, rectangle/sharp, block
 * - Custom variant styles: contained, outlined, text (customizable via variantStyleMap)
 * - Icon support (startIcon, endIcon)
 * - Theme integration
 * - Responsive design
 * - No shadows/elevation by default (matches theme)
 *
 * ## Standard Variants
 * - `contained`: Button with solid background
 * - `outlined`: Button with border and transparent background
 * - `text`: Button with no border or background
 *
 * ## Custom Shape Variants
 * Additional shape-related variants can be applied via the `shape` prop:
 *
 * ### pill
 * Softer than default but not a circle.
 * - borderRadius: 12–16px
 *
 * ### circle
 * Perfect circle for icon-only buttons (play, plus, close).
 * - borderRadius: 50%
 * - minWidth: 40px
 * - padding: 0
 *
 * ### square
 * Exact square shape with equal width and height for all sizes.
 * - borderRadius: 0.5px
 * - width: 40px (medium), 32px (small), 48px (large)
 * - height: 40px (medium), 32px (small), 48px (large)
 *
 * ### rectangle / sharp
 * Minimal radius or zero radius, more "enterprise" look.
 * - borderRadius: 0.5px (rectangle) or 0 (sharp)
 *
 * ### block
 * Full-width button for mobile forms.
 * - width: 100%
 * - display: block
 *
 * ## Customizing Variants
 * You can customize variant styles by modifying the `variantStyleMap` in the `variantStyles` useMemo hook.
 * This allows you to add custom styles for contained, outlined, or text variants.
 *
 * ## Examples
 *
 * ```jsx
 * // Standard button
 * <SolyticsButton variant="contained" color="primary">
 *   Click Me
 * </SolyticsButton>
 *
 * // Pill shape
 * <SolyticsButton variant="contained" shape="pill">
 *   Primary CTA
 * </SolyticsButton>
 *
 * // Icon circle button
 * <SolyticsButton shape="circle" variant="contained">
 *   <AddIcon />
 * </SolyticsButton>
 *
 * // Square button (exact square)
 * <SolyticsButton shape="square" variant="contained">
 *   <Icon />
 * </SolyticsButton>
 *
 * // Rectangle/Sharp button
 * <SolyticsButton shape="rectangle" variant="outlined">
 *   Enterprise Style
 * </SolyticsButton>
 *
 * // Block button (full width)
 * <SolyticsButton shape="block" variant="contained">
 *   Submit Form
 * </SolyticsButton>
 *
 * // With icons
 * <SolyticsButton startIcon={<Icon />} endIcon={<Icon />}>
 *   Button with Icons
 * </SolyticsButton>
 *
 * // With onClick handler
 * <SolyticsButton onClick={() => handleClick()}>
 *   Click Me
 * </SolyticsButton>
 *
 * // With title tooltip
 * <SolyticsButton title="Click to submit form">
 *   Submit
 * </SolyticsButton>
 *
 * // Form button
 * <SolyticsButton type="submit" name="submitBtn" value="submit">
 *   Submit Form
 * </SolyticsButton>
 *
 * // Custom dimensions
 * <SolyticsButton height="50px" width="200px">
 *   Custom Size
 * </SolyticsButton>
 *
 * // With min/max width
 * <SolyticsButton minWidth="150px" maxWidth="300px">
 *   Responsive Width
 * </SolyticsButton>
 * ```
 * */
